import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';

import { getMonths } from 'utilitys/helpers/Date/dateHelper';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { IActivitiesData } from 'api/queries/getDelayDriver.query';

export const useFinishDateFuturePotentialDelayDriversLogic = ({ api, ui, table, title, subTitle }) => {
    const { sendEventWithDimensions } = useEventWithDimensions();
    const { handleSnackBar } = useCustomSnackBar();

    const months = getMonths({
        startDate: api.getActivities.data?.startDate,
        endDate: api.getActivities.data?.finishDate,
    });

    const filteredRows: IActivitiesData[] = useMemo(
        () => api.getActivities.data?.delayDrivers || [],
        [api.getActivities.data],
    );

    const handleOnSearch = useCallback(
        (serachValue: string) => {
            ui.setters.setOnSearch(serachValue);
            table.setPagination({ ...table.pagination, page: 1 });
            sendEventWithDimensions({
                category: 'Activities Table(s)',
                action: 'Search by Activity Name',
                label: serachValue,
            });
        },
        [ui.state.onSearch],
    );

    const handleStatus = useCallback(
        (value: readonly ISelectOption<string>[]) => {
            ui.setters.setTypeFilter(value);
            table.setPagination({ ...table.pagination, page: 1 });
            sendEventWithDimensions({
                category: 'Activities Table(s)',
                action: 'Status Filter click',
                label: value.length > 0 ? value.map((item) => item.label).join(', ') : 'Deselect All',
            });
        },
        [table.pagination],
    );

    const handleStatusType = useCallback(
        (value: readonly ISelectOption<string>[]) => {
            table.setPagination({ ...table.pagination, page: 1 });
            ui.setters.setStatusFilter(value);
        },
        [table.pagination],
    );

    const handleCriticalPath = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const isChecked = event.target.checked;
            table.setPagination({ ...table.pagination, page: 1 });
            ui.setters.setIsCriticalPath(!ui.state.isCriticalPath);
        },
        [table.pagination],
    );

    const handleDelayDriver = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const isChecked = event.target.checked;
            table.setPagination({ ...table.pagination, page: 1 });
            ui.setters.setIsDelayDriver(isChecked);
        },
        [table.pagination],
    );

    const handleDate = useCallback(
        (value: ISelectOption<string> | null) => {
            ui.setters.setDateFilter(value);
            table.setPagination({ ...table.pagination, page: 1 });
        },
        [table.pagination],
    );

    const handleAPICsvExport = () => {
        if (ui.state.latestVersionId) {
            api.extractDelayDriverToCsv.instance.mutate(api.extractDelayDriverToCsv.payload({ title, subTitle }), {
                onSuccess: () => {
                    handleSnackBar('File Successfully Downloaded', 'success');

                    sendEventWithDimensions({
                        category: 'Export',
                        action: 'CSV',
                        label: `${title} ${subTitle}`,
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            });
        }
    };

    useEffect(() => {
        table.setPagination({ ...table.pagination, page: 1 });
    }, [table.sorting]);

    useEffect(() => {
        if (api.getActivities.data) {
            table.setTotalPages(api.getActivities.data.numOfPages);
        }
    }, [api.getActivities.data]);

    return {
        logic: {
            months,
            filteredRows,
            handleDate,
            handleOnSearch,
            handleStatus,
            handleCriticalPath,
            handleDelayDriver,
            handleStatusType,
            handleAPICsvExport,
        },
    };
};
